// frontend/src/components/Reports/AttendanceReportsV2.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useLocation, Link } from 'react-router-dom';
import './AttendanceReports.css';

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const AttendanceReportsV2 = () => {
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const dt = new Date();
    dt.setDate(dt.getDate() - 7);
    return dt;
  }, []);

  // Tab management
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' | 'student' | 'teacher'
  
  // Common filters
  const [startDate, setStartDate] = useState(formatDate(defaultStart));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Daily Summary tab
  const [dailyData, setDailyData] = useState([]);
  const [dailyFilters, setDailyFilters] = useState({
    group_id: '',
    teacher_id: '',
    grade: '',
    reason_code: ''
  });
  
  // Student Profile tab
  const [studentData, setStudentData] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [students, setStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [studentDetailData, setStudentDetailData] = useState(null);
  
  // Teacher Summary tab
  const [teacherData, setTeacherData] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [teachers, setTeachers] = useState([]);
  
  // Reference data
  const [groups, setGroups] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  
  const location = useLocation();

  // Fetch student profile report
  const fetchStudentReport = useCallback(async () => {
    if (!selectedStudentId) return;
    
    setLoading(true);
    setError('');
    try {
      const params = {
        start: startDate,
        end: endDate
      };
      
      const res = await axios.get(`/attendance/v2/reports/student/${selectedStudentId}/`, { params });
      setStudentData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load student report.');
      setStudentData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedStudentId, startDate, endDate]);

  // Fetch student detail data using our new API
  const fetchStudentDetailReport = useCallback(async (studentId) => {
    if (!studentId) return;
    
    setLoading(true);
    setError('');
    try {
      const params = { start_date: startDate, end_date: endDate };
      const res = await axios.get(`/attendance/v2/reports/student-detail/${studentId}/`, { params });
      setStudentDetailData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load student attendance details.');
      setStudentDetailData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Handle URL parameters for student_id (from StudentList link)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const studentIdFromUrl = urlParams.get('student_id');
    
    if (studentIdFromUrl && studentIdFromUrl !== selectedStudentId) {
      setActiveTab('student');
      setSelectedStudentId(studentIdFromUrl);
      fetchStudentDetailReport(studentIdFromUrl);
      
      // Also fetch student info for search term if students are loaded
      if (students.length > 0) {
        const student = students.find(s => s.id.toString() === studentIdFromUrl);
        if (student) {
          setStudentSearchTerm(`${student.first_name} ${student.last_name}`);
        }
      }
    }
  }, [location.search, students, selectedStudentId, fetchStudentDetailReport]);

  // Auto-fetch student report when student is selected via URL
  useEffect(() => {
    if (selectedStudentId && activeTab === 'student') {
      fetchStudentReport();
    }
  }, [selectedStudentId, activeTab, fetchStudentReport]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.student-search-container')) {
        setShowStudentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [teachersRes, gradeLevelsRes, studentsRes] = await Promise.all([
          axios.get('/api/teachers/all/'),
          axios.get('/courses/api/grade_levels/'),
          axios.get('/students/api/list/')
        ]);
        
        setTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : (teachersRes.data?.results || []));
        setGradeLevels(Array.isArray(gradeLevelsRes.data) ? gradeLevelsRes.data : (gradeLevelsRes.data?.results || []));
        setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : (studentsRes.data?.results || []));
      } catch (e) {
        console.warn('Failed to load some reference data:', e);
      }
    };
    loadReferenceData();
  }, []);

  // Fetch daily summary report
  const fetchDailyReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        start: startDate,
        end: endDate,
        ...dailyFilters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const res = await axios.get('/attendance/v2/reports/daily/', { params });
      setDailyData(res.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load daily report.');
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, dailyFilters]);

  // Fetch teacher summary report
  const fetchTeacherReport = useCallback(async () => {
    if (!selectedTeacherId) return;
    
    setLoading(true);
    setError('');
    try {
      const params = {
        start: startDate,
        end: endDate
      };
      
      const res = await axios.get(`/attendance/v2/reports/teacher/${selectedTeacherId}/`, { params });
      setTeacherData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load teacher report.');
      setTeacherData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedTeacherId, startDate, endDate]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
  };

  // Handle filter changes for daily tab
  const handleDailyFilterChange = (key, value) => {
    setDailyFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Filter students based on search term
  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
    const searchLower = studentSearchTerm.toLowerCase();
    return fullName.includes(searchLower) || 
           student.student_id?.toString().includes(searchLower);
  });

  // Handle student selection
  const handleStudentSelect = (student) => {
    setSelectedStudentId(student.id);
    setStudentSearchTerm(`${student.first_name} ${student.last_name}`);
    setShowStudentDropdown(false);
    fetchStudentDetailReport(student.id);
  };

  // Handle search input change
  const handleStudentSearchChange = (e) => {
    setStudentSearchTerm(e.target.value);
    setShowStudentDropdown(true);
    if (!e.target.value) {
      setSelectedStudentId('');
    }
  };

  // Render Daily Summary tab
  const renderDailyTab = () => (
    <div className="tab-content">
      <h3>Daily Summary Report</h3>
      <p>View daily attendance summaries with filtering options.</p>
      
      <div className="filters">
        <div className="filter-row">
          <label>
            Group
            <select 
              value={dailyFilters.group_id} 
              onChange={(e) => handleDailyFilterChange('group_id', e.target.value)}
            >
              <option value="">All Groups</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </label>
          
          <label>
            Teacher
            <select 
              value={dailyFilters.teacher_id} 
              onChange={(e) => handleDailyFilterChange('teacher_id', e.target.value)}
            >
              <option value="">All Teachers</option>
              {teachers.map(teacher => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.first_name} {teacher.last_name}
                </option>
              ))}
            </select>
          </label>
          
          <label>
            Grade
            <select 
              value={dailyFilters.grade} 
              onChange={(e) => handleDailyFilterChange('grade', e.target.value)}
            >
              <option value="">All Grades</option>
              {gradeLevels.map(grade => (
                <option key={grade.id} value={grade.grade}>Grade {grade.grade}</option>
              ))}
            </select>
          </label>
          
          <button onClick={fetchDailyReport} disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>
      
      {dailyData.length > 0 && (
        <div className="results">
          <h4>Results ({dailyData.length} records)</h4>
          <table className="report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Student</th>
                <th>Grade</th>
                <th>Daily Status</th>
                <th>Present</th>
                <th>Absent</th>
                <th>Tardy</th>
                <th>Excused</th>
                <th>Arrival</th>
                <th>Departure</th>
                <th>Unexcused Absences</th>
              </tr>
            </thead>
            <tbody>
              {dailyData.map((record, idx) => (
                <tr key={idx}>
                  <td>{record.date}</td>
                  <td>
                    <Link 
                      to="#" 
                      onClick={() => {
                        setSelectedStudentId(record.student_id);
                        setActiveTab('student');
                      }}
                    >
                      {record.student_name}
                    </Link>
                  </td>
                  <td>{record.student_grade || 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${record.daily_status.toLowerCase()}`}>
                      {record.daily_status_display}
                      {record.is_inferred && <span className="inferred-badge">Inferred</span>}
                    </span>
                  </td>
                  <td>{record.periods_present_count}</td>
                  <td>{record.periods_absent_count}</td>
                  <td>{record.periods_tardy_count}</td>
                  <td>{record.periods_excused_count}</td>
                  <td>{record.first_arrival_time || 'N/A'}</td>
                  <td>{record.last_departure_time || 'N/A'}</td>
                  <td>{record.unexcused_absence_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Render Modern Student Detail View
  const renderModernStudentDetail = () => {
    if (!studentDetailData) return null;
    
    const { student, summary_statistics, daily_timeline, attendance_patterns } = studentDetailData;
    const stats = summary_statistics;
    
    return (
      <div className="modern-student-detail">
        {/* Student Header */}
        <div className="student-detail-header">
          <div className="student-info">
            <div className="student-avatar">
              {student.name.split(' ').map(n => n[0]).join('').toUpperCase()}
            </div>
            <div className="student-details">
              <h2>{student.name}</h2>
              <div className="student-meta">
                <span className="grade-badge">Grade {student.grade}</span>
                <span className="date-range">{studentDetailData.date_range.start} to {studentDetailData.date_range.end}</span>
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <button className="print-btn" onClick={() => window.print()}>🖨️ Print</button>
            <button className="export-btn">📊 Export</button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="metrics-grid">
          <div className={`metric-card daily-rate ${stats.chronic_absent ? 'alert' : 'success'}`}>
            <div className="metric-icon">🎓</div>
            <div className="metric-content">
              <div className="metric-value">{stats.daily_attendance_rate}%</div>
              <div className="metric-label">Daily Attendance Rate</div>
              <div className="metric-subtitle">{stats.present_days} of {stats.total_days} days present</div>
            </div>
          </div>
          
          <div className="metric-card period-rate">
            <div className="metric-icon">📋</div>
            <div className="metric-content">
              <div className="metric-value">{stats.period_attendance_rate}%</div>
              <div className="metric-label">Period Attendance Rate</div>
              <div className="metric-subtitle">{stats.present_periods} of {stats.present_periods + stats.absent_periods} periods</div>
            </div>
          </div>
          
          <div className="metric-card tardies">
            <div className="metric-icon">⏰</div>
            <div className="metric-content">
              <div className="metric-value">{stats.tardy_periods}</div>
              <div className="metric-label">Total Tardies</div>
              <div className="metric-subtitle">Behavioral tracking</div>
            </div>
          </div>
          
          <div className="metric-card absences">
            <div className="metric-icon">📅</div>
            <div className="metric-content">
              <div className="metric-value">{stats.absent_days}</div>
              <div className="metric-label">Absent Days</div>
              <div className="metric-subtitle">{stats.partial_days} partial days</div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {attendance_patterns.length > 0 && (
          <div className="alerts-section">
            <h3>🚨 Attendance Patterns</h3>
            <div className="alerts-grid">
              {attendance_patterns.map((pattern, index) => (
                <div key={index} className={`alert-card ${pattern.severity}`}>
                  <div className="alert-icon">
                    {pattern.severity === 'error' ? '🔴' : pattern.severity === 'warning' ? '🟡' : '🔵'}
                  </div>
                  <div className="alert-content">
                    <div className="alert-title">{pattern.type.replace(/_/g, ' ').toUpperCase()}</div>
                    <div className="alert-description">{pattern.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Timeline */}
        <div className="timeline-section">
          <h3>📊 Recent Attendance History</h3>
          <div className="timeline-grid">
            {daily_timeline.slice(-14).map((day, index) => (
              <div key={index} className={`timeline-day ${day.daily_status.toLowerCase()}`}>
                <div className="day-date">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                <div className="day-status">{day.daily_status}</div>
                <div className="day-details">
                  P:{day.periods_present_count} A:{day.periods_absent_count} T:{day.periods_tardy_count}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section Period Breakdown (like Course breakdown in Infinite Campus) */}
        {studentDetailData.section_period_breakdown && studentDetailData.section_period_breakdown.length > 0 && (
          <div className="section-breakdown-section">
            <h3>📚 Group & Section Period Breakdown</h3>
            <div className="table-container">
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Group & Section</th>
                    <th>Teacher</th>
                    <th>Time</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Tardy</th>
                    <th>Excused</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {studentDetailData.section_period_breakdown.map((section, index) => (
                    <tr key={index}>
                      <td>
                        <div className="section-info">
                          <div className="section-name">{section.group_name}</div>
                          <div className="period-name">{section.period_name}</div>
                        </div>
                      </td>
                      <td>{section.teacher}</td>
                      <td className="time-slot">{section.time_slot}</td>
                      <td className="count-cell present">{section.present_count}</td>
                      <td className="count-cell absent">{section.absent_count}</td>
                      <td className="count-cell tardy">{section.tardy_count}</td>
                      <td className="count-cell excused">{section.excused_count}</td>
                      <td>
                        <span className={`rate-badge ${section.attendance_rate >= 90 ? 'good' : section.attendance_rate >= 80 ? 'warning' : 'poor'}`}>
                          {section.attendance_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detailed Table */}
        <div className="detailed-table-section">
          <h3>📋 Detailed Daily Records</h3>
          <div className="table-container">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Daily Status</th>
                  <th>Present Periods</th>
                  <th>Absent Periods</th>
                  <th>Tardies</th>
                  <th>First Arrival</th>
                  <th>Last Departure</th>
                </tr>
              </thead>
              <tbody>
                {daily_timeline.map((day, index) => (
                  <tr key={index}>
                    <td>{new Date(day.date).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${day.daily_status.toLowerCase()}`}>
                        {day.daily_status}
                      </span>
                    </td>
                    <td>{day.periods_present_count}</td>
                    <td>{day.periods_absent_count}</td>
                    <td>{day.periods_tardy_count}</td>
                    <td>{day.first_arrival_time || 'N/A'}</td>
                    <td>{day.last_departure_time || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Render Student Profile tab
  const renderStudentTab = () => (
    <div className="tab-content">
      <h3>Student Profile Report</h3>
      <p>View detailed attendance timeline and period breakdown for a specific student.</p>
      
      <div className="filters">
        <div className="filter-row">
          <div className="student-search-container">
            <label>
              Search Student
              <input
                type="text"
                value={studentSearchTerm}
                onChange={handleStudentSearchChange}
                onFocus={() => setShowStudentDropdown(true)}
                placeholder="Type student name or ID..."
                className="student-search-input"
              />
            </label>
            
            {showStudentDropdown && studentSearchTerm && (
              <div className="student-dropdown">
                {filteredStudents.length > 0 ? (
                  filteredStudents.slice(0, 10).map(student => (
                    <div
                      key={student.id}
                      className="student-option"
                      onClick={() => handleStudentSelect(student)}
                    >
                      <div className="student-name">
                        {student.first_name} {student.last_name}
                      </div>
                      <div className="student-details">
                        ID: {student.student_id || 'N/A'} | Grade: {student.grade_level?.grade || 'No Grade'}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">No students found</div>
                )}
                {filteredStudents.length > 10 && (
                  <div className="more-results">
                    Showing first 10 of {filteredStudents.length} results...
                  </div>
                )}
              </div>
            )}
          </div>
          
          <button onClick={fetchStudentReport} disabled={loading || !selectedStudentId}>
            {loading ? 'Loading...' : 'Load Student Report'}
          </button>
        </div>
      </div>
      
      {/* Show modern detail view if available, otherwise show legacy view */}
      {studentDetailData ? renderModernStudentDetail() : studentData && (
        <div className="results">
          <div className="student-header">
            <h4>{studentData.student.name} - {studentData.student.grade || 'No Grade'}</h4>
            <div className="date-range">
              {studentData.date_range.start} to {studentData.date_range.end}
            </div>
          </div>
          
          <div className="summary-stats">
            <div className="stat-card">
              <div className="stat-value">{studentData.summary_statistics.attendance_rate}%</div>
              <div className="stat-label">Attendance Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{studentData.summary_statistics.present_days}</div>
              <div className="stat-label">Present Days</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{studentData.summary_statistics.absent_days}</div>
              <div className="stat-label">Absent Days</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{studentData.summary_statistics.partial_days}</div>
              <div className="stat-label">Partial Days</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{studentData.summary_statistics.total_tardies || 0}</div>
              <div className="stat-label">Total Tardies</div>
            </div>
            {studentData.summary_statistics.total_unexcused_tardies && (
              <div className="stat-card">
                <div className="stat-value">{studentData.summary_statistics.total_unexcused_tardies}</div>
                <div className="stat-label">Unexcused Tardies</div>
              </div>
            )}
          </div>
          
          <div className="timeline-section">
            <h5>Daily Timeline</h5>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Daily Status</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Tardy</th>
                  <th>Excused</th>
                  <th>Tardy Equiv. Abs.</th>
                  <th>Arrival</th>
                  <th>Departure</th>
                </tr>
              </thead>
              <tbody>
                {studentData.daily_timeline.map((day, idx) => (
                  <tr key={idx}>
                    <td>{day.date}</td>
                    <td>
                      <span className={`status-badge status-${day.daily_status.toLowerCase()}`}>
                        {day.daily_status_display}
                      </span>
                    </td>
                    <td>{day.periods_present_count}</td>
                    <td>{day.periods_absent_count}</td>
                    <td>{day.periods_tardy_count}</td>
                    <td>{day.periods_excused_count}</td>
                    <td>{day.tardy_equivalent_absences ? parseFloat(day.tardy_equivalent_absences).toFixed(2) : '0.00'}</td>
                    <td>{day.first_arrival_time || 'N/A'}</td>
                    <td>{day.last_departure_time || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="period-section">
            <h5>Period Breakdown</h5>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Teacher</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Minutes Late</th>
                </tr>
              </thead>
              <tbody>
                {studentData.period_breakdown.map((period, idx) => (
                  <tr key={idx}>
                    <td>{period.date}</td>
                    <td>{period.section_period_name}</td>
                    <td>{period.teacher_name || 'N/A'}</td>
                    <td>
                      <span className={`status-badge status-${period.effective_status.toLowerCase()}`}>
                        {period.effective_status_display}
                        {period.is_inferred && <span className="inferred-badge">Inferred</span>}
                      </span>
                    </td>
                    <td>{period.reason_code || 'N/A'}</td>
                    <td>
                      {period.minutes_late ? (
                        <span className="minutes-late">
                          {period.minutes_late} min
                          {period.effective_status === 'T' && <span className="tardy-indicator"> (Tardy)</span>}
                        </span>
                      ) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Render Teacher Summary tab
  const renderTeacherTab = () => (
    <div className="tab-content">
      <h3>Teacher Summary Report</h3>
      <p>View aggregated attendance data for a teacher's sections.</p>
      
      <div className="filters">
        <div className="filter-row">
          <label>
            Select Teacher
            <select 
              value={selectedTeacherId} 
              onChange={(e) => setSelectedTeacherId(e.target.value)}
            >
              <option value="">Choose a teacher...</option>
              {teachers.map(teacher => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.first_name} {teacher.last_name}
                </option>
              ))}
            </select>
          </label>
          
          <button onClick={fetchTeacherReport} disabled={loading || !selectedTeacherId}>
            {loading ? 'Loading...' : 'Load Teacher Report'}
          </button>
        </div>
      </div>
      
      {teacherData && (
        <div className="results">
          <div className="teacher-header">
            <h4>{teacherData.teacher.name}</h4>
            <div className="date-range">
              {teacherData.date_range.start} to {teacherData.date_range.end}
            </div>
          </div>
          
          <div className="summary-stats">
            {/* Daily Student-Based Rate */}
            <div className="stat-card daily-rate">
              <div className="stat-value">{teacherData.overall_statistics.daily_attendance_rate}%</div>
              <div className="stat-label">Daily Attendance Rate</div>
              <div className="stat-subtitle">{teacherData.overall_statistics.daily_present_students} of {teacherData.overall_statistics.total_unique_students} students present</div>
            </div>
            
            {/* Period-Based Rate */}
            <div className="stat-card period-rate">
              <div className="stat-value">{teacherData.overall_statistics.period_attendance_rate}%</div>
              <div className="stat-label">Period Attendance Rate</div>
              <div className="stat-subtitle">{teacherData.overall_statistics.present_count} of {teacherData.overall_statistics.present_count + teacherData.overall_statistics.absent_count} period records</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value">{teacherData.overall_statistics.total_records}</div>
              <div className="stat-label">Total Records</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{teacherData.overall_statistics.present_count}</div>
              <div className="stat-label">Present</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{teacherData.overall_statistics.absent_count}</div>
              <div className="stat-label">Absent</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{teacherData.overall_statistics.tardy_count || 0}</div>
              <div className="stat-label">Tardy (Behavioral)</div>
            </div>
          </div>
          
          {/* Explanation of dual system */}
          <div className="attendance-explanation">
            <h5>Understanding Attendance Rates</h5>
            <div className="explanation-cards">
              <div className="explanation-card daily-explanation">
                <h6>Daily Attendance Rate</h6>
                <p>Counts each student once per day. Used for daily operations and student tracking.</p>
                <p><strong>Example:</strong> If 15 students were present today (regardless of tardies), rate = 100%</p>
              </div>
              <div className="explanation-card period-explanation">
                <h6>Period Attendance Rate</h6>
                <p>Counts attendance per period. Used for section management and intervention.</p>
                <p><strong>Example:</strong> If 1 student missed 1 period out of 28 total period records, rate = 96.43%</p>
              </div>
            </div>
            <p className="tardy-note"><strong>Note:</strong> Tardies are tracked separately for behavioral interventions and do not affect attendance rates per Idaho state law.</p>
          </div>

          <div className="section-aggregates">
            <h5>Section Period Details</h5>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Section Period</th>
                  <th>Total Records</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Tardy</th>
                  <th>Excused</th>
                  <th>Period Rate</th>
                </tr>
              </thead>
              <tbody>
                {teacherData.section_aggregates.map((section, idx) => (
                  <tr key={idx}>
                    <td>{section.section_period_name}</td>
                    <td>{section.total_records}</td>
                    <td>{section.present_count}</td>
                    <td>{section.absent_count}</td>
                    <td>{section.tardy_count}</td>
                    <td>{section.excused_count}</td>
                    <td>{section.period_attendance_rate || section.attendance_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="daily-aggregates">
            <h5>Daily Aggregates</h5>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Total Records</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Tardy</th>
                  <th>Excused</th>
                  <th>Attendance Rate</th>
                </tr>
              </thead>
              <tbody>
                {teacherData.daily_aggregates.map((day, idx) => (
                  <tr key={idx}>
                    <td>{day.date}</td>
                    <td>{day.total_records}</td>
                    <td>{day.present_count}</td>
                    <td>{day.absent_count}</td>
                    <td>{day.tardy_count}</td>
                    <td>{day.excused_count}</td>
                    <td>{day.attendance_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="reports-container">
      <h1>Attendance Reports (V2)</h1>
      <p>Advanced attendance reporting with event logging and cached summaries.</p>

      {/* Common Date Range Filters */}
      <div className="common-filters">
        <div className="filter-row">
          <label>
            Start Date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            End Date
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={activeTab === 'daily' ? 'tab-button active' : 'tab-button'}
          onClick={() => handleTabChange('daily')}
        >
          Daily Summary
        </button>
        <button 
          className={activeTab === 'student' ? 'tab-button active' : 'tab-button'}
          onClick={() => handleTabChange('student')}
        >
          Student Profile
        </button>
        <button 
          className={activeTab === 'teacher' ? 'tab-button active' : 'tab-button'}
          onClick={() => handleTabChange('teacher')}
        >
          Teacher Summary
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Tab Content */}
      {activeTab === 'daily' && renderDailyTab()}
      {activeTab === 'student' && renderStudentTab()}
      {activeTab === 'teacher' && renderTeacherTab()}

      <style>{`
        .reports-container { 
          max-width: 1200px; 
          margin: 30px auto; 
          padding: 20px; 
          background: #fff; 
          border-radius: 8px; 
          box-shadow: 0 2px 8px rgba(0,0,0,0.08); 
        }
        
        .common-filters { 
          margin-bottom: 20px; 
          padding: 15px; 
          background: #f8f9fa; 
          border-radius: 6px; 
        }
        
        .tab-navigation { 
          display: flex; 
          gap: 2px; 
          margin-bottom: 20px; 
          border-bottom: 2px solid #e0e0e0; 
        }
        
        .tab-button { 
          padding: 12px 20px; 
          background: #f5f5f5; 
          border: none; 
          border-radius: 6px 6px 0 0; 
          cursor: pointer; 
          font-weight: 500; 
          transition: all 0.2s; 
        }
        
        .tab-button:hover { 
          background: #e0e0e0; 
        }
        
        .tab-button.active { 
          background: #1976d2; 
          color: white; 
        }
        
        .tab-content { 
          min-height: 400px; 
        }
        
        .filters { 
          margin-bottom: 20px; 
          padding: 15px; 
          background: #f8f9fa; 
          border-radius: 6px; 
        }
        
        .filter-row { 
          display: flex; 
          gap: 16px; 
          flex-wrap: wrap; 
          margin-bottom: 10px; 
          align-items: flex-end; 
        }
        
        label { 
          display: flex; 
          flex-direction: column; 
          font-size: 0.9rem; 
          color: #333; 
          font-weight: 500; 
        }
        
        input, select { 
          padding: 8px 12px; 
          border: 1px solid #ccc; 
          border-radius: 6px; 
          min-width: 180px; 
          margin-top: 4px; 
        }
        
        button { 
          padding: 10px 16px; 
          background: #1976d2; 
          color: #fff; 
          border: none; 
          border-radius: 6px; 
          cursor: pointer; 
          font-weight: 500; 
          transition: background 0.2s; 
        }
        
        button:hover { 
          background: #1565c0; 
        }
        
        button[disabled] { 
          opacity: 0.6; 
          cursor: not-allowed; 
        }
        
        .error { 
          color: #d32f2f; 
          background: #ffebee; 
          padding: 12px; 
          border-radius: 6px; 
          margin: 10px 0; 
          border-left: 4px solid #d32f2f; 
        }
        
        .report-table { 
          width: 100%; 
          border-collapse: collapse; 
          margin-top: 15px; 
          background: white; 
          border-radius: 6px; 
          overflow: hidden; 
          box-shadow: 0 1px 3px rgba(0,0,0,0.1); 
        }
        
        .report-table th, .report-table td { 
          border: 1px solid #e0e0e0; 
          padding: 12px; 
          text-align: left; 
          font-size: 0.9rem; 
        }
        
        .report-table th { 
          background: #f5f5f5; 
          font-weight: 600; 
          color: #333; 
        }
        
        .report-table tbody tr:hover { 
          background: #f8f9fa; 
        }
        
        .status-badge { 
          display: inline-flex; 
          align-items: center; 
          gap: 4px; 
          padding: 4px 8px; 
          border-radius: 12px; 
          font-size: 0.8rem; 
          font-weight: 500; 
        }
        
        .status-present { 
          background: #e8f5e8; 
          color: #2e7d32; 
        }
        
        .status-absent { 
          background: #ffebee; 
          color: #c62828; 
        }
        
        .status-tardy { 
          background: #fff3e0; 
          color: #ef6c00; 
        }
        
        .status-excused { 
          background: #e3f2fd; 
          color: #1565c0; 
        }
        
        .status-partial { 
          background: #f3e5f5; 
          color: #7b1fa2; 
        }
        
        .status-unknown { 
          background: #f5f5f5; 
          color: #616161; 
        }
        
        .inferred-badge { 
          background: #fff; 
          color: #666; 
          font-size: 0.7rem; 
          padding: 1px 4px; 
          border-radius: 8px; 
          border: 1px solid #ddd; 
        }
        
        .summary-stats { 
          display: flex; 
          gap: 16px; 
          margin: 20px 0; 
          flex-wrap: wrap; 
        }
        
        .stat-card { 
          background: #f8f9fa; 
          padding: 16px; 
          border-radius: 8px; 
          text-align: center; 
          min-width: 120px; 
          border: 1px solid #e0e0e0; 
        }
        
        .stat-value { 
          font-size: 1.8rem; 
          font-weight: bold; 
          color: #1976d2; 
          margin-bottom: 4px; 
        }
        
        .stat-label { 
          font-size: 0.85rem; 
          color: #666; 
          font-weight: 500; 
        }
        
        .student-header, .teacher-header { 
          background: #f8f9fa; 
          padding: 16px; 
          border-radius: 6px; 
          margin-bottom: 20px; 
        }
        
        .date-range { 
          color: #666; 
          font-size: 0.9rem; 
          margin-top: 4px; 
        }
        
        .timeline-section, .period-section, .section-aggregates, .daily-aggregates { 
          margin-top: 30px; 
        }
        
        .timeline-section h5, .period-section h5, .section-aggregates h5, .daily-aggregates h5 { 
          color: #333; 
          margin-bottom: 10px; 
          padding-bottom: 5px; 
          border-bottom: 2px solid #e0e0e0; 
        }
        
        .results h4 { 
          color: #1976d2; 
          margin-bottom: 15px; 
        }
        
        .student-search-container { 
          position: relative; 
          display: flex; 
          flex-direction: column; 
        }
        
        .student-search-input { 
          min-width: 300px; 
        }
        
        .student-dropdown { 
          position: absolute; 
          top: 100%; 
          left: 0; 
          right: 0; 
          background: white; 
          border: 1px solid #ccc; 
          border-radius: 6px; 
          box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
          max-height: 300px; 
          overflow-y: auto; 
          z-index: 1000; 
          margin-top: 2px; 
        }
        
        .student-option { 
          padding: 12px; 
          cursor: pointer; 
          border-bottom: 1px solid #f0f0f0; 
          transition: background 0.2s; 
        }
        
        .student-option:hover { 
          background: #f8f9fa; 
        }
        
        .student-option:last-child { 
          border-bottom: none; 
        }
        
        .student-name { 
          font-weight: 500; 
          color: #333; 
          margin-bottom: 2px; 
        }
        
        .student-details { 
          font-size: 0.85rem; 
          color: #666; 
        }
        
        .no-results, .more-results { 
          padding: 12px; 
          text-align: center; 
          color: #666; 
          font-style: italic; 
        }
        
        .more-results { 
          background: #f8f9fa; 
          border-top: 1px solid #e0e0e0; 
          font-size: 0.85rem; 
        }
      `}</style>
    </div>
  );
};

export default AttendanceReportsV2;
